/**
 * Date utility functions for timezone conversion and formatting
 * Supports Australia/Sydney timezone and browser timezone fallback
 */

/**
 * Get the user's preferred timezone
 * Defaults to Australia/Sydney, falls back to browser timezone
 * @param {boolean} useAustraliaSydney - Whether to force Australia/Sydney timezone
 * @returns {string} - IANA timezone identifier
 */
export const getUserTimezone = (useAustraliaSydney = true) => {
  if (useAustraliaSydney) {
    return 'Australia/Sydney';
  }
  
  // Fallback to browser timezone
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Could not detect browser timezone, falling back to Australia/Sydney');
    return 'Australia/Sydney';
  }
};

/**
 * Convert a date to the specified timezone
 * @param {Date|string|number} date - Date to convert
 * @param {string} timezone - Target timezone (IANA identifier)
 * @returns {Date} - Date object in the target timezone
 */
export const convertToTimezone = (date, timezone = 'Australia/Sydney') => {
  if (!date) return null;
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date');
    }
    
    // Create a new date object with timezone offset applied
    const utc = dateObj.getTime() + (dateObj.getTimezoneOffset() * 60000);
    const targetTime = new Date(utc + (getTimezoneOffset(timezone) * 60000));
    
    return targetTime;
  } catch (error) {
    console.error('Error converting date to timezone:', error);
    return new Date(date); // Return original date as fallback
  }
};

/**
 * Get timezone offset in milliseconds
 * @param {string} timezone - IANA timezone identifier
 * @returns {number} - Offset in milliseconds
 */
const getTimezoneOffset = (timezone) => {
  try {
    const now = new Date();
    const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const target = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    return target.getTime() - utc.getTime();
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    return 0;
  }
};

/**
 * Format date for display with timezone conversion
 * @param {Date|string|number} date - Date to format
 * @param {Object} options - Formatting options
 * @param {string} options.timezone - Target timezone
 * @param {string} options.format - Format type ('date', 'time', 'datetime', 'relative')
 * @param {boolean} options.useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, options = {}) => {
  const {
    timezone = getUserTimezone(options.useAustraliaSydney),
    format = 'date',
  } = options;
  
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const formatOptions = getFormatOptions(format);
    
    return new Intl.DateTimeFormat('en-AU', {
      ...formatOptions,
      timeZone: timezone
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error);
    return new Date(date).toLocaleDateString();
  }
};

/**
 * Get formatting options based on format type
 * @param {string} format - Format type
 * @returns {Object} - Intl.DateTimeFormat options
 */
const getFormatOptions = (format) => {
  switch (format) {
    case 'date':
      return {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };
    case 'time':
      return {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
    case 'datetime':
      return {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
    case 'relative':
      return {}; // Will be handled separately
    case 'short':
      return {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit'
      };
    case 'long':
      return {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };
    default:
      return {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      };
  }
};

/**
 * Format date as relative time (e.g., "2 hours ago", "in 3 days")
 * @param {Date|string|number} date - Date to format
 * @param {Object} options - Options
 * @param {string} options.timezone - Target timezone
 * @param {boolean} options.useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (date, options = {}) => {
  const {
    timezone = getUserTimezone(options.useAustraliaSydney),
    useAustraliaSydney = true
  } = options;
  
  if (!date) return '';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }
    
    const now = new Date();
    const diffInSeconds = Math.floor((now - dateObj) / 1000);
    
    // If more than 7 days, show absolute date
    if (Math.abs(diffInSeconds) > 7 * 24 * 60 * 60) {
      return formatDate(date, { timezone, format: 'date', useAustraliaSydney });
    }
    
    // Use Intl.RelativeTimeFormat for relative formatting
    const rtf = new Intl.RelativeTimeFormat('en-AU', { numeric: 'auto' });
    
    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(-diffInSeconds, 'second');
    } else if (Math.abs(diffInSeconds) < 3600) {
      return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    } else if (Math.abs(diffInSeconds) < 86400) {
      return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    } else {
      return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return formatDate(date, { timezone, format: 'date', useAustraliaSydney });
  }
};

/**
 * Format date for job posting timestamps
 * @param {Date|string|number} date - Date to format
 * @param {boolean} useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {string} - Formatted timestamp
 */
export const formatJobTimestamp = (date, useAustraliaSydney = true) => {
  if (!date) return 'Just now';
  
  const now = new Date();
  const dateObj = new Date(date);
  const diffInHours = Math.floor((now - dateObj) / (1000 * 60 * 60));
  
  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInHours < 168) { // Less than a week
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(date, { 
      format: 'date', 
      useAustraliaSydney 
    });
  }
};

/**
 * Format time for WhatsApp-style messages
 * @param {Date|string|number} date - Date to format
 * @param {boolean} useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {string} - Formatted time (e.g., "2:30 PM")
 */
export const formatWhatsAppTime = (date, useAustraliaSydney = true) => {
  return formatDate(date, { 
    format: 'datetime', 
    useAustraliaSydney 
  });
};

/**
 * Check if a date is today
 * @param {Date|string|number} date - Date to check
 * @param {boolean} useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {boolean} - True if date is today
 */
export const isToday = (date, useAustraliaSydney = true) => {
  if (!date) return false;
  
  try {
    const timezone = getUserTimezone(useAustraliaSydney);
    const today = new Date();
    const dateObj = new Date(date);
    
    const todayStr = formatDate(today, { format: 'short', timezone, useAustraliaSydney });
    const dateStr = formatDate(dateObj, { format: 'short', timezone, useAustraliaSydney });
    
    return todayStr === dateStr;
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};

/**
 * Get current date in specified timezone
 * @param {boolean} useAustraliaSydney - Whether to use Australia/Sydney timezone
 * @returns {Date} - Current date in specified timezone
 */
export const getCurrentDate = (useAustraliaSydney = true) => {
  const timezone = getUserTimezone(useAustraliaSydney);
  return convertToTimezone(new Date(), timezone);
};

// Default export with all utilities
const dateUtilsExport = {
  getUserTimezone,
  convertToTimezone,
  formatDate,
  formatRelativeTime,
  formatJobTimestamp,
  formatWhatsAppTime,
  isToday,
  getCurrentDate
};
export default dateUtilsExport;